import { formatPrice } from '../utils/formatPrice';
import NavBar from "../components/NavBar";
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API } from '../context/AuthContext';

const ICON_PATHS = {
  trendDown: <><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"/><polyline points="16 17 22 17 22 11"/></>,
  clock: <><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></>,
  check: <><polyline points="20 6 9 17 4 12"/></>
};

function Icon({ name, size = 16, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      {ICON_PATHS[name]}
    </svg>
  );
}

export default function TaxSnapshot() {
  const { user, logout } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [lots, setLots] = useState([]);
  const [goals, setGoals] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [portfolioRes, goalsRes, lotsRes] = await Promise.all([
        axios.get(`${API}/api/prices/portfolio`),
        axios.get(`${API}/api/wealth`),
        axios.get(`${API}/api/holdings/lots`)
      ]);
      setPortfolio(portfolioRes.data);
      setGoals(goalsRes.data.goals || {});
      setLots(lotsRes.data.lots || []);
    } catch (err) {
      console.error('Tax snapshot load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => '$' + Math.round(n || 0).toLocaleString();
  const fmtPct = (n) => (n >= 0 ? '+' : '') + (n || 0).toFixed(1) + '%';

  const STATE_TAX_RATES = { "California": 0.133, "New York": 0.109, "New Jersey": 0.1075, "Oregon": 0.099, "Minnesota": 0.0985, "Vermont": 0.0875, "Iowa": 0.086, "Wisconsin": 0.0765, "Maine": 0.0715, "Idaho": 0.058, "South Carolina": 0.07, "Montana": 0.069, "Maryland": 0.0575, "North Dakota": 0.029, "Georgia": 0.055, "Kentucky": 0.05, "Michigan": 0.0425, "Illinois": 0.0495, "Indiana": 0.0323, "Pennsylvania": 0.0307, "North Carolina": 0.0499, "Colorado": 0.044, "Utah": 0.0485, "Massachusetts": 0.05, "Arizona": 0.025, "Missouri": 0.048, "Virginia": 0.0575, "Ohio": 0.04, "Hawaii": 0.11, "Connecticut": 0.0699, "Delaware": 0.066, "Rhode Island": 0.0599, "Oklahoma": 0.0475, "Kansas": 0.057, "Nebraska": 0.0684, "West Virginia": 0.065, "Louisiana": 0.06, "Arkansas": 0.055, "Mississippi": 0.05, "Alabama": 0.05, "New Mexico": 0.059 };
  const stateTaxRate = STATE_TAX_RATES[goals?.state_of_residence] || 0;
  const getTaxRates = () => {
    const bracket = goals?.income_bracket || '22';
    const rates = {
      '10': { short: 0.10, long: 0.00 },
      '12': { short: 0.12, long: 0.00 },
      '22': { short: 0.22, long: 0.15 },
      '24': { short: 0.24, long: 0.15 },
      '32': { short: 0.32, long: 0.15 },
      '35': { short: 0.35, long: 0.20 },
      '37': { short: 0.37, long: 0.20 },
    };
    const base = rates[bracket] || rates["22"];
    return { short: base.short + stateTaxRate, long: base.long + stateTaxRate };
  };

  const getDaysHeld = (dateStr) => {
    const now = new Date();
    const nowUTC = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    const parts = dateStr.split("T")[0].split("-");
    const purchasedUTC = new Date(Date.UTC(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2])));
    return Math.floor((nowUTC - purchasedUTC) / (1000 * 60 * 60 * 24));
  };

  const isLongTerm = (dateStr) => getDaysHeld(dateStr) > 365;
  const daysToLongTerm = (dateStr) => Math.max(0, 365 - getDaysHeld(dateStr));

  if (loading) return <div style={s.loadingPage}>Loading tax snapshot...</div>;

  const holdings = portfolio?.holdings || [];
  const rates = getTaxRates();

  let totalShortGain = 0;
  let totalLongGain = 0;
  let totalValue = 0;
  let totalCost = 0;

  const grouped = {};
  holdings.forEach(h => {
    const sym = h.coin_symbol;
    if (!grouped[sym]) grouped[sym] = { symbol: sym, holdings: [], allLots: [], totalValue: 0, totalCost: 0, totalGain: 0 };
    const hLots = lots.filter(l => parseInt(l.holding_id) === parseInt(h.id));
    const currentPrice = h.current_price || 0;
    grouped[sym].totalValue += h.current_value || 0;
    grouped[sym].totalCost += h.cost_basis || 0;
    grouped[sym].totalGain += h.unrealized_gain || 0;
    totalValue += h.current_value || 0;
    totalCost += h.cost_basis || 0;
    if (hLots.length > 0) {
      hLots.forEach(lot => {
        const lotVal = parseFloat(lot.amount) * currentPrice;
        const lotCost = parseFloat(lot.amount) * parseFloat(lot.buy_price);
        const lotGain = lotVal - lotCost;
        const lt = isLongTerm(lot.buy_date);
        const dtlt = daysToLongTerm(lot.buy_date);
        const taxRate = lt ? rates.long : rates.short;
        const taxOwed = lotGain > 0 ? lotGain * taxRate : 0;
        const savings = lotGain > 0 && !lt ? lotGain * (rates.short - rates.long) : 0;
        if (lt) totalLongGain += lotGain > 0 ? lotGain : 0;
        else totalShortGain += lotGain > 0 ? lotGain : 0;
        grouped[sym].allLots.push({ ...lot, lotVal, lotCost, lotGain, lt, dtlt, taxOwed, savings });
      });
    } else {
      const gain = h.unrealized_gain || 0;
      if (isLongTerm(h.created_at)) totalLongGain += gain > 0 ? gain : 0;
      else totalShortGain += gain > 0 ? gain : 0;
    }
    grouped[sym].holdings.push(h);
  });
  const groupedArr = Object.values(grouped);
  groupedArr.forEach(group => {
    group.allLots.sort((a, b) => {
      if (a.lt && !b.lt) return -1;
      if (!a.lt && b.lt) return 1;
      if (!a.lt && !b.lt) return a.dtlt - b.dtlt;
      return 0;
    });
  });

  const shortTax = totalShortGain * rates.short;
  const longTax = totalLongGain * rates.long;
  const totalTax = shortTax + longTax;
  const totalGain = totalValue - totalCost;
  const afterTax = totalValue - totalTax;
  const maxGain = Math.max(totalShortGain, totalLongGain, 1);

  return (
    <div style={s.page}>
      <NavBar />
      <style>{`
        .ts-input:focus { border-color: #4ade80 !important; box-shadow: 0 0 0 3px rgba(74,222,128,0.15); }
        .ts-lot:hover { box-shadow: 0 10px 22px -10px rgba(16,40,28,0.14); }
        * { box-sizing: border-box; }
      `}</style>

      <div style={s.content}>
        <div style={s.sectionLabel}>Tax estimate</div>
        <h1 style={s.pageTitle}>Tax snapshot</h1>
        <p style={s.pageDesc}>Estimated federal {stateTaxRate > 0 ? "+ " + goals.state_of_residence + " state" : ""} tax if you sold everything today. Based on your {goals?.income_bracket || "22"}% federal bracket{stateTaxRate > 0 ? " + " + Math.round(stateTaxRate * 100) + "% state" : ""}.</p>

        <div style={s.metrics}>
          <div style={s.metric}><div style={s.mLabel}>Total value</div><div style={s.mValue}>{fmt(totalValue)}</div><div style={s.mSub}>If sold today</div></div>
          <div style={s.metric}><div style={s.mLabel}>Total gain</div><div style={{...s.mValue, color: totalGain >= 0 ? '#1a6b4a' : '#dc2626'}}>{fmt(totalGain)}</div><div style={s.mSub}>{fmtPct((totalGain/(totalCost||1))*100)}</div></div>
          <div style={s.metric}><div style={s.mLabel}>Est. tax owed</div><div style={{...s.mValue, color:'#dc2626'}}>{fmt(totalTax)}</div><div style={s.mSub}>{stateTaxRate > 0 ? "Federal + " + goals.state_of_residence + " state" : "Federal only"}</div></div>
          <div style={s.metricHero}><div style={{...s.mLabel, color:'rgba(255,255,255,0.6)'}}>You actually keep</div><div style={{...s.mValueHero}}>{fmt(afterTax)}</div><div style={{...s.mSub, color:'rgba(255,255,255,0.5)'}}>After taxes</div></div>
        </div>

        <div style={s.breakdown}>
          <div style={s.bTitle}>Short-term vs long-term gains</div>
          {[
            { label: 'Short-term gains', desc: `Held less than 1 year — ${Math.round(rates.short*100)}% rate`, value: totalShortGain, tax: shortTax, color: '#d97706' },
            { label: 'Long-term gains', desc: `Held more than 1 year — ${Math.round(rates.long*100)}% rate`, value: totalLongGain, tax: longTax, color: '#1a6b4a' }
          ].map(row => (
            <div key={row.label} style={s.bRow}>
              <div style={s.bRowTop}>
                <div><div style={s.bLabel}>{row.label}</div><div style={s.bDesc}>{row.desc}</div></div>
                <div style={s.bRight}><div style={s.bGain}>{fmt(row.value)}</div><div style={s.bTax}>Tax: {fmt(row.tax)}</div></div>
              </div>
              <div style={s.bBarTrack}>
                <div style={{...s.bBarFill, width: `${Math.max(2, Math.round((row.value / maxGain) * 100))}%`, background: row.color}} />
              </div>
            </div>
          ))}
        </div>

        {(() => {
          const harvestOpportunities = groupedArr.filter(g => g.totalGain < -500);
          if (harvestOpportunities.length === 0) return null;
          const totalHarvestableLoss = harvestOpportunities.reduce((sum, g) => sum + Math.abs(g.totalGain), 0);
          const gainToOffset = Math.max(0, totalGain);
          const lossToUse = Math.min(totalHarvestableLoss, gainToOffset + 3000);
          const taxSavings = lossToUse * ((parseFloat(goals?.income_bracket || 22) / 100) + stateTaxRate);
          return (
            <div style={s.harvestCard}>
              <div style={s.harvestHeader}>
                <div style={s.harvestIconChip}><Icon name="trendDown" size={20} color="#92400e" /></div>
                <div>
                  <div style={s.harvestLabel}>Tax loss harvesting opportunity</div>
                  <div style={s.harvestTitle}>Harvest losses to save ~{fmt(taxSavings)} in taxes</div>
                </div>
              </div>
              <div style={s.harvestDesc}>You have {fmt(totalHarvestableLoss)} in unrealized losses across {harvestOpportunities.length} position{harvestOpportunities.length > 1 ? "s" : ""}. Harvesting these losses offsets {fmt(totalHarvestableLoss)} of your taxable gains — reducing your tax bill by ~{fmt(taxSavings)} at your {Math.round((parseFloat(goals?.income_bracket || 22) / 100 + stateTaxRate) * 100)}% combined rate.</div>
              <div style={{display:"flex", flexDirection:"column", gap:"8px"}}>
                {harvestOpportunities.map(g => (
                  <div key={g.symbol} style={s.harvestRow}>
                    <div style={{display:"flex", alignItems:"center", gap:"10px"}}>
                      <div style={s.harvestBadge}>{g.symbol}</div>
                      <div>
                        <div style={s.harvestSym}>{g.symbol}</div>
                        <div style={s.harvestOffset}>Offsets {fmt(Math.abs(g.totalGain))} of taxable gains</div>
                      </div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={s.harvestSavings}>~{fmt(Math.abs(g.totalGain) * ((parseFloat(goals?.income_bracket || 22) / 100) + stateTaxRate))} tax savings</div>
                      <div style={s.harvestOffset}>if harvested today</div>
                    </div>
                  </div>
                ))}
              </div>
              <p style={s.harvestNote}>Note: Tax loss harvesting involves selling at a loss to offset gains. Beware the wash sale rule — do not repurchase the same asset within 30 days. Consult a CPA before acting.</p>
            </div>
          );
        })()}

        <div style={s.section}>
          <h2 style={s.sTitle}>Holding breakdown</h2>
          {holdings.length === 0 ? (
            <p style={s.empty}>No holdings yet. <a href="/onboarding" style={s.link}>Set up your portfolio</a></p>
          ) : groupedArr.map(group => (
            <div key={group.symbol} style={s.hCard}>
              <div style={s.hHeader}>
                <div style={s.hLeft}>
                  <div style={s.badge}>{group.symbol}</div>
                  <div>
                    <div style={s.hName}>{group.symbol}</div>
                    <div style={s.hDetail}>{group.holdings.reduce((sum,h) => sum + parseFloat(h.amount), 0).toLocaleString()} coins total · {group.holdings.length} lot{group.holdings.length > 1 ? "s" : ""}</div>
                  </div>
                </div>
                <div style={s.hRight}>
                  <div style={s.hVal}>{fmt(group.totalValue)}</div>
                  <div style={{...s.hGain, color: group.totalGain >= 0 ? "#1a6b4a" : "#dc2626"}}>{fmt(group.totalGain)} ({fmtPct((group.totalGain/(group.totalCost||1))*100)})</div>
                </div>
              </div>
              {group.allLots.length > 0 ? (
                <div>
                  <div style={s.lotHeader}>Tax lots ({group.allLots.length})</div>
                  {group.allLots.map((lot, i) => (
                    <div key={i} className="ts-lot" style={lot.lt ? s.lotCardLong : s.lotCardShort}>
                      <div style={s.lotTop}>
                        <div style={s.lotInfo}>
                          <span style={s.lotAmt}>{parseFloat(lot.amount).toLocaleString()} coins</span>
                          <span style={s.lotDate}>Purchased {new Date(lot.buy_date).toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'})}</span>
                          <span style={s.lotPrice}>@ ${formatPrice(parseFloat(lot.buy_price))}</span>
                        </div>
                        <span style={{...s.termBadge, background: lot.lt ? 'rgba(74,222,128,0.14)' : '#fef3c7', color: lot.lt ? '#1a6b4a' : '#92400e'}}>
                          {lot.lt ? <><Icon name="check" size={11} /> Long-term</> : 'Short-term'}
                        </span>
                      </div>
                      <div style={s.lotStats}>
                        <div style={s.stat}><div style={s.sLabel}>Lot value</div><div style={s.sVal}>{fmt(lot.lotVal)}</div></div>
                        <div style={s.stat}><div style={s.sLabel}>Gain/loss</div><div style={{...s.sVal, color: lot.lotGain>=0?'#1a6b4a':'#dc2626'}}>{fmt(lot.lotGain)}</div></div>
                        <div style={s.stat}><div style={s.sLabel}>Est. tax</div><div style={{...s.sVal, color:'#dc2626'}}>{fmt(lot.taxOwed)}</div></div>
                        <div style={s.stat}><div style={s.sLabel}>You keep</div><div style={{...s.sVal, color:'#1a6b4a'}}>{fmt(lot.lotVal - lot.taxOwed)}</div></div>
                      </div>
                      {!lot.lt && lot.dtlt > 0 && (
                        <div style={s.countdown}>
                          <Icon name="clock" size={14} color="#92400e" /> Hold <strong style={{margin:'0 4px'}}>{lot.dtlt} more days</strong> to flip to long-term rate
                          {lot.savings > 0 && <span style={s.savings}> — saves you {fmt(lot.savings)}</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={s.noLots}>
                  No purchase date recorded. <a href="/settings" style={s.link}>Add purchase date in Settings</a> for lot-level tracking.
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={s.disclaimer}>
          {stateTaxRate > 0 ? "Federal and " + goals.state_of_residence + " state tax estimates included." : "Federal estimates only. State taxes not included."} Wash sale rule and AMT not included. Not tax advice. Consult a licensed CPA before making financial decisions.
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', background:'#fafaf8', fontFamily:"'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif" },
  loadingPage: { minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#fafaf8', color:'#6b6b6b', fontFamily:"'Inter', system-ui, sans-serif" },
  content: { maxWidth:'900px', margin:'0 auto', padding:'3rem 2rem' },
  sectionLabel: { fontSize:'12px', fontWeight:'600', letterSpacing:'0.08em', textTransform:'uppercase', color:'#1a6b4a', marginBottom:'0.6rem' },
  pageTitle: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'32px', fontWeight:'400', color:'#1a1a1a', marginBottom:'0.5rem', letterSpacing:'-0.01em' },
  pageDesc: { fontSize:'15px', color:'#6b6b6b', marginBottom:'2rem', lineHeight:'1.6', maxWidth:'640px' },

  metrics: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(170px, 1fr))', gap:'14px', marginBottom:'1.75rem' },
  metric: { background:'#fff', borderRadius:'14px', padding:'1.25rem', border:'1px solid rgba(0,0,0,0.07)', boxShadow:'0 2px 6px rgba(16,40,28,0.05)' },
  metricHero: { background:'linear-gradient(135deg, #0f2a1c 0%, #06100b 100%)', borderRadius:'14px', padding:'1.25rem', boxShadow:'0 14px 28px -12px rgba(5,20,12,0.4)' },
  mLabel: { fontSize:'11px', fontWeight:'600', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'6px' },
  mValue: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'25px', fontWeight:'600', color:'#1a1a1a' },
  mValueHero: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'25px', fontWeight:'600', color:'#fff' },
  mSub: { fontSize:'11px', color:'#9b9b9b', marginTop:'3px' },

  breakdown: { background:'#fff', borderRadius:'16px', padding:'1.75rem', border:'1px solid rgba(0,0,0,0.07)', boxShadow:'0 2px 6px rgba(16,40,28,0.06)', marginBottom:'1.5rem' },
  bTitle: { fontSize:'14px', fontWeight:'700', color:'#1a1a1a', marginBottom:'1.25rem' },
  bRow: { padding:'1rem 0', borderBottom:'1px solid rgba(0,0,0,0.06)' },
  bRowTop: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'10px' },
  bLabel: { fontSize:'15px', fontWeight:'700', color:'#1a1a1a', marginBottom:'4px' },
  bDesc: { fontSize:'13px', color:'#6b6b6b' },
  bRight: { textAlign:'right' },
  bGain: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'17px', fontWeight:'600', color:'#1a1a1a' },
  bTax: { fontSize:'13px', color:'#dc2626', marginTop:'2px' },
  bBarTrack: { height:'8px', background:'#f4f1eb', borderRadius:'99px', overflow:'hidden' },
  bBarFill: { height:'100%', borderRadius:'99px', transition:'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)' },

  harvestCard: { background:'#fef3c7', border:'1px solid rgba(146,64,14,0.18)', borderRadius:'16px', padding:'1.75rem', marginBottom:'1.5rem' },
  harvestHeader: { display:'flex', alignItems:'center', gap:'14px', marginBottom:'0.75rem' },
  harvestIconChip: { width:'44px', height:'44px', borderRadius:'12px', background:'rgba(146,64,14,0.12)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },
  harvestLabel: { fontSize:'12px', fontWeight:'700', color:'#92400e', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'3px' },
  harvestTitle: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'21px', fontWeight:'600', color:'#1a1a1a' },
  harvestDesc: { fontSize:'14px', color:'#6b6b6b', marginBottom:'1.25rem', lineHeight:'1.6' },
  harvestRow: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 14px', background:'#fff', borderRadius:'10px', boxShadow:'0 1px 2px rgba(16,40,28,0.05)' },
  harvestBadge: { width:'34px', height:'34px', borderRadius:'50%', background:'#fee2e2', color:'#dc2626', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'10px', fontWeight:'700' },
  harvestSym: { fontSize:'14px', fontWeight:'700', color:'#1a1a1a' },
  harvestOffset: { fontSize:'12px', color:'#6b6b6b' },
  harvestSavings: { fontSize:'14px', fontWeight:'700', color:'#1a6b4a' },
  harvestNote: { fontSize:'12px', color:'#92400e', marginTop:'1rem', lineHeight:'1.6' },

  section: { background:'#fff', borderRadius:'16px', padding:'1.75rem', border:'1px solid rgba(0,0,0,0.07)', boxShadow:'0 2px 6px rgba(16,40,28,0.06)', marginBottom:'1.5rem' },
  sTitle: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'20px', fontWeight:'500', color:'#1a1a1a', marginBottom:'1.25rem' },
  hCard: { border:'1px solid rgba(0,0,0,0.07)', borderRadius:'14px', padding:'1.25rem', marginBottom:'1.5rem', background:'#fff', boxShadow:'0 2px 6px rgba(16,40,28,0.05)' },
  hHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', background:'#f9f9f7', padding:'0.75rem 1rem', borderRadius:'10px', margin:'-0.25rem -0.25rem 1rem' },
  hLeft: { display:'flex', alignItems:'center', gap:'12px' },
  badge: { width:'40px', height:'40px', borderRadius:'50%', background:'rgba(74,222,128,0.16)', color:'#1a6b4a', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'700' },
  hName: { fontSize:'15px', fontWeight:'700', color:'#1a1a1a' },
  hDetail: { fontSize:'12px', color:'#6b6b6b', marginTop:'2px' },
  hRight: { textAlign:'right' },
  hVal: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'17px', fontWeight:'600', color:'#1a1a1a' },
  hGain: { fontSize:'12px', marginTop:'2px', fontWeight:'600' },
  lotHeader: { fontSize:'12px', fontWeight:'700', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'0.75rem' },
  lotCardLong: { background:'#f9f9f7', borderRadius:'12px', padding:'1rem', marginBottom:'0.75rem', borderLeft:'3px solid #1a6b4a', transition:'box-shadow 0.15s ease' },
  lotCardShort: { background:'#f9f9f7', borderRadius:'12px', padding:'1rem', marginBottom:'0.75rem', borderLeft:'3px solid #EF9F27', transition:'box-shadow 0.15s ease' },
  lotTop: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem', flexWrap:'wrap', gap:'8px' },
  lotInfo: { display:'flex', gap:'12px', alignItems:'center', flexWrap:'wrap' },
  lotAmt: { fontSize:'14px', fontWeight:'700', color:'#1a1a1a' },
  lotDate: { fontSize:'13px', color:'#6b6b6b' },
  lotPrice: { fontSize:'13px', color:'#6b6b6b' },
  termBadge: { display:'inline-flex', alignItems:'center', gap:'4px', fontSize:'11px', padding:'4px 10px', borderRadius:'99px', fontWeight:'700', whiteSpace:'nowrap' },
  lotStats: { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(100px, 1fr))', gap:'8px', marginBottom:'0.5rem' },
  stat: { background:'#fff', borderRadius:'9px', padding:'0.65rem' },
  sLabel: { fontSize:'10px', color:'#6b6b6b', textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:'3px' },
  sVal: { fontFamily:"'Fraunces', Georgia, serif", fontSize:'16px', fontWeight:'600', color:'#1a1a1a' },
  countdown: { display:'flex', alignItems:'center', gap:'6px', background:'#fef3c7', borderRadius:'9px', padding:'8px 12px', fontSize:'13px', color:'#92400e', flexWrap:'wrap', marginTop:'0.5rem' },
  savings: { color:'#1a6b4a', fontWeight:'700', marginLeft:'4px' },
  noLots: { background:'#f9f9f7', borderRadius:'9px', padding:'0.75rem', fontSize:'13px', color:'#6b6b6b' },
  empty: { fontSize:'14px', color:'#6b6b6b' },
  link: { color:'#1a6b4a', textDecoration:'none', fontWeight:'600' },
  disclaimer: { fontSize:'12px', color:'#9b9b9b', lineHeight:'1.7', textAlign:'center', padding:'1rem 0 2rem' }
};
